let csrfToken: string | null = null;

export function setCsrfToken(token: string | null) {
  csrfToken = token;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
    public details?: unknown,
  ) {
    super(message);
  }
}

export async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers);
  if (!(options.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }
  if (csrfToken) headers.set("X-CSRF-Token", csrfToken);
  const res = await fetch(`/api/v1${path}`, { ...options, headers, credentials: "include" });
  if (!res.ok) {
    let payload: any = null;
    try {
      payload = await res.json();
    } catch {
      /* no body */
    }
    throw new ApiError(
      payload?.error?.message ?? (payload?.message ? `${payload.message} (${res.status})` : `Request failed (${res.status})`),
      res.status,
      payload?.error?.code,
      payload?.error?.details,
    );
  }
  return res.json() as Promise<T>;
}

export async function apiBlob(path: string): Promise<Blob> {
  const headers = new Headers();
  if (csrfToken) headers.set("X-CSRF-Token", csrfToken);
  const res = await fetch(`/api/v1${path}`, { headers, credentials: "include" });
  if (!res.ok) throw new ApiError(`Download failed (${res.status})`, res.status);
  return res.blob();
}

export async function bootstrapAuth(): Promise<{ user: AuthUser | null; csrfToken: string | null }> {
  const data = await api<{ user: AuthUser | null; csrfToken: string | null }>("/auth/me");
  setCsrfToken(data.csrfToken);
  return data;
}

export async function login(email: string, password: string) {
  const data = await api<{ user: AuthUser; csrfToken: string }>("/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
  setCsrfToken(data.csrfToken);
  return data.user;
}

export async function logout() {
  try {
    await api("/auth/logout", { method: "POST", body: JSON.stringify({}) });
  } finally {
    setCsrfToken(null);
  }
}

export type AuthUser = {
  id: string;
  organizationId: string;
  email: string;
  name: string;
  role: string;
};
